import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { apiGet, apiPut, apiUploadImage } from "../../shared/apiClient";
import { getApiBaseUrl } from "../../config/appSettings";
import { useToast } from "../../shared/ToastContext";
import { createBlogQuillModules } from "./blogQuillConfig";
import { BlogEditorFormFields, defaultBlogForm, toLocalDatetime } from "./BlogEditorFormFields";

function RefreshCwIcon({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
      <path d="M21 3v5h-5" />
      <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
      <path d="M3 21v-5h5" />
    </svg>
  );
}

function FileViewIcon({ className = "h-4 w-4" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M4 4h7l3 3v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V5a1 1 0 0 1 1-1z" />
      <line x1="7" y1="8" x2="11" y2="8" />
      <line x1="7" y1="12" x2="14" y2="12" />
    </svg>
  );
}

function DocumentCheckIcon({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <path d="m9 15 2 2 4-4" />
    </svg>
  );
}

function EyeIcon({ className = "h-5 w-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

export function EditBlogPage() {
  const { id: idParam } = useParams();
  const id = idParam && /^\d+$/.test(String(idParam)) ? Number(idParam) : NaN;
  const { user } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const isAdmin = String(user?.role || "").toLowerCase() === "admin";
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...defaultBlogForm });
  const [htmlMode, setHtmlMode] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const quillRef = useRef(null);

  const toastRef = useRef(toast);
  toastRef.current = toast;
  const imageHandler = useCallback(() => {
    const input = document.createElement("input");
    input.setAttribute("type", "file");
    input.setAttribute("accept", "image/*");
    input.click();
    input.onchange = async () => {
      const file = input.files?.[0];
      if (!file) return;
      const r = await apiUploadImage("/api/Uploads/image", file);
      if (!r.ok) {
        toastRef.current.push(String(r.error), "error");
        return;
      }
      const quill = quillRef.current?.getEditor();
      if (!quill) return;
      const range = quill.getSelection(true) ?? { index: quill.getLength() };
      const path = r.data?.path ?? "";
      const base = getApiBaseUrl();
      const url = path.startsWith("http") ? path : `${base}${path}`;
      quill.insertEmbed(range.index, "image", url);
    };
  }, []);

  const onEditorImageFiles = useCallback(async (files) => {
    const quill = quillRef.current?.getEditor();
    if (!quill) return;
    const base = getApiBaseUrl();
    let index = (quill.getSelection(true) ?? { index: Math.max(0, quill.getLength() - 1) }).index;
    for (const file of files) {
      if (!file.type.startsWith("image/")) continue;
      const r = await apiUploadImage("/api/Uploads/image", file);
      if (!r.ok) {
        toastRef.current.push(String(r.error), "error");
        continue;
      }
      const path = r.data?.path ?? "";
      const url = path.startsWith("http") ? path : `${base}${path}`;
      quill.insertEmbed(index, "image", url);
      index += 1;
    }
    quill.setSelection(index, 0);
  }, []);

  const modules = useMemo(() => createBlogQuillModules({ imageHandler }), [imageHandler]);

  useEffect(() => {
    void (async () => {
      if (isAdmin) {
        const c = await apiGet("/api/Customers");
        if (c.ok) setCustomers(c.data ?? []);
      }
    })();
  }, [isAdmin]);

  const loadBlog = useCallback(async () => {
    if (!Number.isFinite(id) || id <= 0) {
      toast.push("Invalid blog", "error");
      navigate("/blogs", { replace: true });
      return;
    }
    setLoading(true);
    const res = await apiGet(`/api/Blogs/${id}`);
    if (!res.ok) {
      toast.push(String(res.error), "error");
      navigate("/blogs", { replace: true });
      setLoading(false);
      return;
    }
    const b = res.data;
    setForm({
      id: b.id,
      customerId: String(b.customerId),
      title: b.title ?? "",
      subtitle: b.subtitle ?? "",
      shortDescription: b.shortDescription ?? "",
      date: toLocalDatetime(b.date) || toLocalDatetime(new Date().toISOString()),
      authorName: b.authorName ?? "",
      image: b.image ?? "",
      content: b.content ?? ""
    });
    setLoading(false);
  }, [id, navigate, toast]);

  useEffect(() => {
    void loadBlog();
  }, [loadBlog]);

  const handleRefresh = async () => {
    if (!Number.isFinite(id) || id <= 0) return;
    setRefreshing(true);
    if (isAdmin) {
      const c = await apiGet("/api/Customers");
      if (c.ok) setCustomers(c.data ?? []);
    }
    const res = await apiGet(`/api/Blogs/${id}`);
    if (res.ok) {
      const b = res.data;
      setForm({
        id: b.id,
        customerId: String(b.customerId),
        title: b.title ?? "",
        subtitle: b.subtitle ?? "",
        shortDescription: b.shortDescription ?? "",
        date: toLocalDatetime(b.date) || toLocalDatetime(new Date().toISOString()),
        authorName: b.authorName ?? "",
        image: b.image ?? "",
        content: b.content ?? ""
      });
    } else {
      toast.push(String(res.error), "error");
    }
    setRefreshing(false);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.title?.trim()) {
      toast.push("Title is required", "error");
      return;
    }
    const customerId = Number(form.customerId);
    if (!Number.isFinite(customerId)) {
      toast.push("Customer is required", "error");
      return;
    }
    setSaving(true);
    const bodyBase = {
      title: form.title,
      subtitle: form.subtitle || null,
      shortDescription: form.shortDescription || null,
      date: form.date ? new Date(form.date).toISOString() : new Date().toISOString(),
      authorName: form.authorName || null,
      image: form.image || null,
      content: form.content || null
    };
    const res = await apiPut(`/api/Blogs/${id}`, {
      id,
      customerId,
      ...bodyBase
    });
    if (!res.ok) toast.push(String(res.error), "error");
    else {
      toast.push("Blog saved", "success");
      navigate("/blogs");
    }
    setSaving(false);
  };

  if (!Number.isFinite(id) || id <= 0) {
    return null;
  }

  if (loading) {
    return (
      <div className="flex w-full items-center justify-center gap-2 py-20 text-sm text-slate-500">
        <span
          className="inline-block h-5 w-5 animate-spin rounded-full border-2 border-slate-200 border-t-brand-600"
          aria-hidden
        />
        Loading…
      </div>
    );
  }

  return (
    <div className="w-full space-y-4">
      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-xl font-semibold text-slate-900 sm:text-2xl">Edit blog</h1>
            <p className="mt-1 text-sm text-slate-500">Update fields below, then save changes.</p>
          </div>
          <div className="flex shrink-0 items-center justify-end gap-2">
            <Link
              to={`/blogs/${id}`}
              className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-cyan-600 shadow-sm transition hover:bg-cyan-50 hover:text-cyan-700"
              title="View on page"
              aria-label="View on page"
            >
              <FileViewIcon className="h-5 w-5" />
            </Link>
            <button
              type="button"
              onClick={() => navigate("/blogs")}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-indigo-600 shadow-sm transition hover:bg-indigo-50 hover:text-indigo-700"
              title="Blogs"
              aria-label="Go to blogs list"
            >
              <EyeIcon className="h-5 w-5" />
            </button>
            <button
              type="button"
              onClick={() => void handleRefresh()}
              disabled={refreshing}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white text-sky-600 shadow-sm transition hover:bg-sky-50 hover:text-sky-700 disabled:cursor-not-allowed disabled:opacity-50"
              title="Refresh"
              aria-label="Refresh from server"
            >
              {refreshing ? (
                <span
                  className="inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-sky-200 border-t-sky-600"
                  aria-hidden
                />
              ) : (
                <RefreshCwIcon className="h-5 w-5" />
              )}
            </button>
            <button
              type="submit"
              form="edit-blog-form"
              disabled={saving}
              className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-brand-800 text-white shadow-md transition hover:bg-brand-700 focus:outline-none focus:ring-2 focus:ring-brand-600/40 disabled:opacity-50"
              title="Save changes"
              aria-label={saving ? "Saving" : "Save changes"}
            >
              {saving ? (
                <span
                  className="inline-block h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-white/30 border-t-white"
                  aria-hidden
                />
              ) : (
                <DocumentCheckIcon className="h-5 w-5" />
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
        <form id="edit-blog-form" onSubmit={onSubmit} className="space-y-0">
          <BlogEditorFormFields
            form={form}
            setForm={setForm}
            isAdmin={isAdmin}
            customers={customers}
            htmlMode={htmlMode}
            setHtmlMode={setHtmlMode}
            quillRef={quillRef}
            modules={modules}
            onEditorImageFiles={onEditorImageFiles}
            onContentImportError={(msg) => toast.push(String(msg), "error")}
            onEditorToolbarImage={imageHandler}
          />
        </form>
      </div>
    </div>
  );
}
